<template>
  <div class="relative group">
    <MessageInput v-if="!showNameField" ref="messageField" v-model="message" @submit="onSubmit" :mention-enabled="isLoggedIn" />

    <template v-else>
      <FormKit type="form" @submit="onSubmit" data-testid="viewer-comment-display-name-form" :config="{ preserveErrors: true }" :actions="false">
        <FormKit type="text" v-model="displayName" data-testid="viewer-comment-display-name-input" inner-class="p-[5px] max-w-[420px]" placeholder="Your name">
          <template #suffix>
            <Button
              :submit="true"
              class="input-button"
              type="solid"
              :variant="displayName ? 'blue' : 'gray'"
              icon="general.paperPlane"
              :disabled="!displayName"
            ></Button>
          </template>
          <template #help>
            <span v-if="list.length" class="text-sm text-slate-500 dark:text-zinc500"
              >Please
              <a href="#" class="text-link" @click.stop.prevent="showSignUpModal()">Sign up</a>
              or
              <a href="#" class="text-link" @click.stop.prevent="showSignInModal()">Sign in</a> if you want to edit or delete your comments.</span
            >
          </template>
        </FormKit>
        <FormKitMessages :node="inputDisplayNameRef?.node" />
      </FormKit>
    </template>
  </div>
</template>
<script lang="ts" setup>
import { ref, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { FormKit, FormKitMessages } from '@formkit/vue';
import { useUserStore } from '@/store/useUserStore';
import { useStorage } from '@vueuse/core';
import { useCommentsStore } from '@/modules/viewer/store/useCommentsStore';
import { useAuthModals } from '@/composables/useAuthModals';

import { Button } from '@/storybook/button';
import MessageInput from './MessageInput.vue';

const commentsStore = useCommentsStore();
const userStore = useUserStore();
const showNameField = ref(false);
const messageField = ref<InstanceType<typeof MessageInput> | null>(null);
const displayName = useStorage('viewer-display-name', undefined);
const inputDisplayNameRef = ref<any>();

const { isLoggedIn } = storeToRefs(userStore);
const { list } = storeToRefs(commentsStore);

const { showSignInModal, showSignUpModal } = useAuthModals();
const emit = defineEmits<{ addedComment: [{ comment: string }] }>();

const message = ref<string>('');

const onSubmit = () => {
  if (!isLoggedIn.value && !displayName.value) {
    showNameField.value = true;
    return Promise.resolve();
  }

  return commentsStore.add(message.value, displayName.value).then(() => {
    emit('addedComment', { comment: message.value });
    showNameField.value = false;

    message.value = '';

    nextTick(() => {
      messageField.value?.resetForm();
    });

    return Promise.resolve();
  });
};
</script>
<style>
.custom-textarea {
  resize: none;
}
.textarea-button {
  @apply absolute right-[5px] bottom-[5px] !rounded-lg;
}
.input-button {
  @apply !rounded-lg;
}
.text-link {
  @apply text-blue-600 dark:text-blue-500 rounded-sm hover:text-blue-700 hover:underline focus-visible:ring focus-visible:ring-blue-500/50 focus-visible:bg-blue-500/10 focus:outline-none;
}
</style>
